import React, { useState, useEffect } from "react";
import Select from "react-select";
import { selectStyles } from "../Constants";

const formatCategory = (category) => {
  if (!category) return "";
  if (category === "noc") return "Notice of Charges";
  if (category === "invoices") return "Invoice List";
  if (category === "transmittal") return "Transmittal Letters";
  if (category === "acknowledgement") return "Acknowledgement Receipts";
  if (category === "legal-advice") return "Legal Advice List";
  if (category === "sec-res") return "Secretarial Resolutions";
  return "";
};

const InvoiceList = ({
  invoices,
  handleRowClick,
  handleDownload,
  handleViewDocument,
  handleDownloadAll,
  handleEmail,
  handleDelete,
  category,
}) => {
  // filter state
  const [selectedType, setSelectedType] = useState("all");
  const [sortKey, setSortKey] = useState(null);
  const [selectedBillTo, setSelectedBillTo] = useState("all");
  const [selectedIssuer, setSelectedIssuer] = useState("all");
  const currentYear = new Date().getFullYear().toString();
  const [selectedYear, setSelectedYear] = useState(currentYear);

  // sort state
  const [sortField, setSortField] = useState(null);
  const [sortAsc, setSortAsc] = useState(true);

  // view state
  const [viewMode, setViewMode] = useState("table");

  // pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // build last 6 years list
  const yearOptions = Array.from({ length: 6 }, (_, i) =>
    (new Date().getFullYear() - i).toString()
  );

  // reset filters when category changes
  useEffect(() => {
    setSelectedType("all");
    setSelectedBillTo("all");
    setSelectedYear(currentYear);
    setCurrentPage(1); // Reset to first page
  }, [category, currentYear]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [
    selectedType,
    selectedBillTo,
    selectedIssuer,
    selectedYear,
    itemsPerPage,
  ]);

  const categoryFiltered = invoices.filter((inv) => {
    const no = inv.invoiceNo || "";
    if (category === "noc") return no.startsWith("NC");
    if (category === "transmittal") return no.startsWith("TL");
    if (category === "acknowledgement") return no.startsWith("AR");
    if (category === "sec-res") return no.startsWith("SR");
    if (category === "legal-advice") return no.startsWith("LA");
    if (category === "invoices")
      return (
        !no.startsWith("NC") &&
        !no.startsWith("TL") &&
        !no.startsWith("AR") &&
        !no.startsWith("SR") &&
        !no.startsWith("LA")
      );

    // Default fallback - exclude all special types
    return (
      !no.startsWith("TL") &&
      !no.startsWith("AR") &&
      !no.startsWith("NC") &&
      !no.startsWith("SR") &&
      !no.startsWith("LA")
    );
  });

  const typeOptions = [
    { value: "all", label: "All Types" },
    ...[...new Set(categoryFiltered.map((i) => i.header))].map((t) => ({
      value: t,
      label: t,
    })),
  ];

  const billToOptions = [
    { value: "all", label: "All Recipients" },
    ...[...new Set(categoryFiltered.map((i) => i?.to?.name))].map((name) => ({
      value: name,
      label: name,
    })),
  ];

  const yearOptionsFormatted = [
    { value: "all", label: "All Years" },
    ...yearOptions.map((y) => ({ value: y, label: y })),
  ];

  const filteredInvoices = categoryFiltered.filter((inv) => {
    const invYear = inv.date ? inv.date.slice(0, 4) : "";
    const yearMatch = selectedYear === "all" || invYear === selectedYear;
    const typeMatch = selectedType === "all" || inv.header === selectedType;

    const billToMatch =
      selectedBillTo === "all" || inv?.to?.name === selectedBillTo;
    return yearMatch && typeMatch && billToMatch;
  });

  const sortedInvoices = [...filteredInvoices].sort((a, b) => {
    if (sortField === "invoiceNo") {
      const cmp = a.invoiceNo.localeCompare(b.invoiceNo, undefined, {
        numeric: true,
        sensitivity: "base",
      });
      return sortAsc ? cmp : -cmp;
    }
    if (sortField === "date") {
      const dateA = new Date(a.date || 0);
      const dateB = new Date(b.date || 0);
      return sortAsc ? dateA - dateB : dateB - dateA;
    }
    if (sortField === "header") {
      const cmp = a.header.localeCompare(b.header);
      return sortAsc ? cmp : -cmp;
    }
    // default: by header then name
    if (a.header < b.header) return -1;
    if (a.header > b.header) return 1;
    const nameA = a.to?.name || a.closing?.name || "";
    const nameB = b.to?.name || b.closing?.name || "";
    if (nameA < nameB) return -1;
    if (nameA > nameB) return 1;
    return 0;
  });

  // Pagination calculations
  const totalItems = sortedInvoices.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedInvoices = sortedInvoices.slice(startIndex, endIndex);
  const clearFilters = () => {
    setSelectedType("all");
    setSelectedBillTo("all");
    setSelectedIssuer("all");
    setSelectedYear(currentYear);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(true);
    }

    setSortKey(field);
  };

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return <span className="text-alt-color ml-1">↕</span>;
    }
    return <span className="text-white ml-1">{sortAsc ? "↑" : "↓"}</span>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusColor = (invoiceNo) => {
    // You can implement your own status logic here
    if (invoiceNo?.startsWith("TL")) return "bg-blue-100 text-blue-800";
    if (invoiceNo?.startsWith("AR")) return "bg-green-100 text-green-800";
    return "bg-amber-100 text-amber-800";
  };

  // Pagination controls component
  const PaginationControls = () => (
    <div className="flex items-center flex-col sm:flex-row sm:gap-0 gap-4 justify-between px-6 py-3 bg-none border-t dark:border-gray-600 border-gray-200">
      <div className="flex items-center space-x-2">
        <span className="text-sm text-alt-color">Show</span>
        <select
          value={itemsPerPage}
          onChange={(e) => setItemsPerPage(Number(e.target.value))}
          className="px-2 py-1 border border-gray-300 rounded-md text-sm bg-inherit focus:outline-none focus:ring-2 focus:ring-tier-secondary"
        >
          <option value={6}>6</option>
          <option value={12}>12</option>
          <option value={24}>24</option>
          <option value={48}>48</option>
          <option value={96}>96</option>
        </select>
        <span className="text-xs sm:text-sm text-alt-color">
          entries | Showing {startIndex + 1} to {Math.min(endIndex, totalItems)}{" "}
          of {totalItems} entries
        </span>
      </div>

      <div className="flex items-center space-x-2">
        <button
          onClick={() => setCurrentPage(1)}
          disabled={currentPage === 1}
          className="px-2 py-1 text-sm text-tier-primary bg-white border border-gray-300 rounded hover:bg-tier-secondary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span className="material-symbols-outlined text-sm">first_page</span>
        </button>
        <button
          onClick={() => setCurrentPage(currentPage - 1)}
          disabled={currentPage === 1}
          className="px-2 py-1 text-sm text-tier-primary bg-white border border-gray-300 rounded hover:bg-tier-secondary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span className="material-symbols-outlined text-sm">
            chevron_left
          </span>
        </button>

        <div className="flex items-center space-x-1">
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNum;
            if (totalPages <= 5) {
              pageNum = i + 1;
            } else if (currentPage <= 3) {
              pageNum = i + 1;
            } else if (currentPage >= totalPages - 2) {
              pageNum = totalPages - 4 + i;
            } else {
              pageNum = currentPage - 2 + i;
            }

            return (
              <button
                key={pageNum}
                onClick={() => setCurrentPage(pageNum)}
                className={`px-3 py-1 text-sm rounded ${
                  currentPage === pageNum
                    ? "bg-tier-secondary text-white"
                    : "text-tier-primary bg-white border border-gray-300 hover:bg-gray-50"
                }`}
              >
                {pageNum}
              </button>
            );
          })}
        </div>

        <button
          onClick={() => setCurrentPage(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="px-2 py-1 text-sm text-tier-primary bg-white border border-gray-300 rounded hover:bg-tier-secondary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span className="material-symbols-outlined text-sm">
            chevron_right
          </span>
        </button>
        <button
          onClick={() => setCurrentPage(totalPages)}
          disabled={currentPage === totalPages}
          className="px-2 py-1 text-sm text-tier-primary bg-white border border-gray-300 rounded hover:bg-tier-secondary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span className="material-symbols-outlined text-sm">last_page</span>
        </button>
      </div>
    </div>
  );

  // Card View Component
  const CardView = () => (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-2">
        {paginatedInvoices.map((inv) => (
          <div
            key={inv.id}
            className="custom-card"
            onClick={() =>
              handleRowClick(
                inv.invoiceNo,
                inv.id,
                inv.ackType || "acknowledgement"
              )
            }
          >
            <div className="p-4">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <span
                    className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                      inv.invoiceNo
                    )}`}
                  >
                    {inv.invoiceNo}
                  </span>
                </div>
                <div className="text-xs text-alt-color">
                  {formatDate(inv.date)}
                </div>
              </div>

              <h3 className="font-semibold text-color mb-2 line-clamp-2">
                {inv.header}
              </h3>

              <div className="space-y-1 text-sm text-alt-color mb-4">
                {category === "invoices" && (
                  <div className="flex items-center">
                    <span className="font-medium w-16">Bill To:</span>
                    <span className="truncate">{inv?.to?.name || "N/A"}</span>
                  </div>
                )}
                <div className="flex items-center">
                  <span className="font-medium w-16">Issuer:</span>
                  <span className="truncate">
                    {inv?.from?.name || inv?.closing?.name || "N/A"}
                  </span>
                </div>
              </div>

              <div className="flex justify-end space-x-2">
                <button
                  className="action-btn-tb action-btn-edit"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewDocument(inv);
                  }}
                  title="Preview"
                >
                  <span className="material-symbols-outlined text-sm">
                    visibility
                  </span>
                </button>
                <button
                  className="action-btn-tb action-btn-download"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownload(inv);
                  }}
                  title="Download"
                >
                  <span className="material-symbols-outlined text-sm">
                    download
                  </span>
                </button>
                <button
                  className="action-btn-tb action-btn-compare"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEmail(inv);
                  }}
                  title="Email"
                >
                  <span className="material-symbols-outlined text-sm">
                    mail
                  </span>
                </button>
                <button
                  className="action-btn-tb action-btn-delete"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(inv.id, category);
                  }}
                  title="Delete"
                >
                  <span className="material-symbols-outlined text-sm">
                    delete
                  </span>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {totalItems > 0 && (
        <div className="mt-4 bg-white/10 rounded-lg shadow-sm border dark:border-gray-600 border-alt-color overflow-hidden">
          <PaginationControls />
        </div>
      )}
    </>
  );

  // Table View Component
  const TableView = () => (
    <div className="mt-2 bg-white/10 rounded-lg shadow-sm border dark:border-gray-600 border-alt-color overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-alt-color dark:divide-gray-600">
          <thead className="bg-tier-secondary">
            <tr>
              <th
                className={`th-custom ${
                  sortKey === "invoiceNo"
                    ? "text-white bg-dark-alt hover:bg-tier-secondary"
                    : ""
                }`}
                onClick={() => handleSort("invoiceNo")}
              >
                <div className="flex items-center">
                  Invoice No.
                  {getSortIcon("invoiceNo")}
                </div>
              </th>
              <th
                className={`th-custom ${
                  sortKey === "header"
                    ? "text-white bg-dark-alt hover:bg-tier-secondary"
                    : ""
                }`}
                onClick={() => handleSort("header")}
              >
                <div className="flex items-center">
                  Type
                  {getSortIcon("header")}
                </div>
              </th>
              <th
                className={`th-custom ${
                  sortKey === "date"
                    ? "text-white bg-dark-alt hover:bg-tier-secondary"
                    : ""
                }`}
                onClick={() => handleSort("date")}
              >
                <div className="flex items-center">
                  Date
                  {getSortIcon("date")}
                </div>
              </th>
              <th className="px-6 py-2 text-left text-xs font-medium text-tier-primary uppercase tracking-wider">
                {category === "invoices" ? "Bill" : "Addressed"} To
              </th>
              <th className="px-6 py-2 text-left text-xs font-medium text-tier-primary uppercase tracking-wider">
                Issuer
              </th>
              <th className="px-6 py-2 text-left text-xs font-medium text-tier-primary uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white/5 divide-y divide-alt-color dark:divide-gray-600">
            {paginatedInvoices.length > 0 ? (
              paginatedInvoices.map((inv, index) => (
                <tr
                  key={inv.id}
                  className={`cursor-pointer hover:bg-tier-secondary/10 dark:hover:bg-dark-alt/10 transition-colors ${
                    index % 2 === 0
                      ? " dark:bg-tier-primary/60"
                      : "bg-tier-primary dark:bg-tier-primary-dark"
                  }`}
                  onClick={() =>
                    handleRowClick(
                      inv.invoiceNo,
                      inv.id,
                      inv.ackType || "acknowledgement"
                    )
                  }
                >
                  <td className="px-6 py-3 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                        inv.invoiceNo
                      )}`}
                    >
                      {inv.invoiceNo}
                    </span>
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap">
                    <div className="text-sm font-medium text-color truncate overflow-hidden whitespace-nowrap max-w-[160px]">
                      {inv.header}
                    </div>
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap">
                    <div className="text-sm text-alt-color">
                      {formatDate(inv.date)}
                    </div>
                  </td>

                  <td className="px-6 py-3 whitespace-nowrap">
                    <div className="text-sm text-color truncate overflow-hidden whitespace-nowrap max-w-[160px]">
                      {inv?.to?.name || "N/A"}
                    </div>
                  </td>

                  <td className="px-6 py-3 whitespace-nowrap">
                    <div className="text-sm text-color truncate overflow-hidden whitespace-nowrap max-w-[160px]">
                      {inv?.from?.name || inv?.closing?.name || "N/A"}
                    </div>
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      <button
                        className="action-btn-tb action-btn-edit"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewDocument(inv);
                        }}
                        title="Preview"
                      >
                        <span className="material-symbols-outlined text-sm">
                          visibility
                        </span>
                      </button>
                      <button
                        className="action-btn-tb action-btn-download"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownload(inv);
                        }}
                        title="Download"
                      >
                        <span className="material-symbols-outlined text-sm">
                          download
                        </span>
                      </button>
                      <button
                        className="action-btn-tb action-btn-compare"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEmail(inv);
                        }}
                        title="Email"
                      >
                        <span className="material-symbols-outlined text-sm">
                          mail
                        </span>
                      </button>
                      <button
                        className="action-btn-tb action-btn-delete"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(inv.id, category);
                        }}
                        title="Delete"
                      >
                        <span className="material-symbols-outlined text-sm">
                          delete
                        </span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={6}
                  className="px-6 py-12 text-center text-gray-500"
                >
                  <div className="flex flex-col items-center">
                    <span className="material-symbols-outlined text-4xl text-gray-300 mb-2">
                      description
                    </span>
                    <p className="text-sm">Empty {formatCategory(category)}.</p>
                    <p className="text-xs text-gray-400 mt-1">
                      Try adjusting your filters or create new.
                    </p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {totalItems > 0 && <PaginationControls />}
    </div>
  );

  return (
    <>
      <div className="px-2 py-4 pt-6 max-w-6xl w-full mx-auto">
        {/* Header */}
        <div className="flex flex-col items-start text-color">
          <div className="flex w-full items-center justify-between mb-4">
            <h2 className="text-xl sm:text-2xl font-bold text-color">
              {formatCategory(category)}
            </h2>
            <div className="flex items-center space-x-2 sm:space-x-4">
              <div className="flex items-center bg-inherit border border-gray-400 dark:border-gray-600 rounded-lg p-1">
                <button
                  onClick={() => setViewMode("table")}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                    viewMode === "table"
                      ? "bg-white/10 text-color shadow-sm"
                      : "text-alt-color hover:text-color"
                  }`}
                >
                  <span className="material-symbols-outlined text-sm mr-1">
                    table_view
                  </span>
                  <span className="sm:inline hidden">Table</span>
                </button>
                <button
                  onClick={() => setViewMode("cards")}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                    viewMode === "cards"
                      ? "bg-white/10 text-color shadow-sm"
                      : "text-alt-color hover:text-color"
                  }`}
                >
                  <span className="material-symbols-outlined text-sm mr-1">
                    grid_view
                  </span>
                  <span className="sm:inline hidden">Cards</span>
                </button>
              </div>
              <div className="flex items-center px-3 py-1 bg-amber-200/20 text-dark-alt rounded-full text-xs sm:text-sm font-medium">
                {sortedInvoices.length} document
                {sortedInvoices.length !== 1 ? "s" : ""}
              </div>
            </div>
          </div>

          {/* Enhanced Filters */}
          <div className="w-full bg-white/5 rounded-lg shadow-sm border dark:border-gray-600 border-gray-200 p-4 mb-4">
            <div className="flex flex-wrap gap-4 items-end">
              {/* Type Filter */}
              <div className="flex-1 min-w-[150px]">
                <label className="block text-sm font-medium text-alt-color mb-1">
                  Type
                </label>
                <Select
                  value={typeOptions.find((opt) => opt.value === selectedType)}
                  onChange={(selected) => setSelectedType(selected.value)}
                  options={typeOptions}
                  styles={selectStyles}
                  classNamePrefix="react-select"
                  isSearchable
                />
              </div>

              {/* Bill To / Issuer Filter */}
              <div className="flex-1 min-w-[150px]">
                <label className="block text-sm font-medium text-alt-color mb-1">
                  {category === "invoices" ? "Bill" : "Addressed"} To
                </label>
                <Select
                  value={billToOptions.find(
                    (opt) => opt.value === selectedBillTo
                  )}
                  onChange={(selected) => setSelectedBillTo(selected.value)}
                  options={billToOptions}
                  styles={selectStyles}
                  classNamePrefix="react-select"
                  isSearchable
                />
              </div>

              {/* Year Filter */}
              <div className="flex-1 min-w-[120px]">
                <label className="block text-sm font-medium text-alt-color mb-1">
                  Year
                </label>
                <Select
                  value={yearOptionsFormatted.find(
                    (opt) => opt.value === selectedYear
                  )}
                  onChange={(selected) => setSelectedYear(selected.value)}
                  options={yearOptionsFormatted}
                  styles={selectStyles}
                  classNamePrefix="react-select"
                  isSearchable
                />
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-2">
                <button onClick={clearFilters} className="btn-action-white">
                  Clear Filters
                </button>
                {(category === "invoices" || category === "noc") && (
                  <button
                    onClick={() => handleDownloadAll(sortedInvoices)}
                    className="btn-action"
                  >
                    <span className="material-symbols-outlined text-sm mr-1">
                      download
                    </span>
                    Download All
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        {viewMode === "table" ? <TableView /> : <CardView />}
      </div>
    </>
  );
};

export default InvoiceList;
